import React, { useEffect, useMemo, useRef, useState } from "react";
import RoleBasedNavigation from "../../components/ui/RoleBasedNavigation";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Select from "../../components/ui/Select";
import CustomerTable from "./components/CustomerTable";
import FilterToolbar from "./components/FilterToolbar";
import ExcelUploadModal from "./components/ExcelUploadModal";
import AddCustomerModal from "./components/AddCustomerModal";
import GenerateQRModal from "./components/GenerateQRModal";
import BulkActionsBar from "./components/BulkActionsBar";
import { generateQRCardsPDF } from "./utils/generateQRCardsPDF";
import { exportCustomersToExcel } from "./utils/exportCustomers";
import { generateWorkCompletionReport } from "./utils/generateWorkCompletionReport";
import { generateSurveyReport } from "./utils/generateSurveyReport";
import { downloadCustomerPhotosZip } from "./utils/downloadCustomerPhotosZip";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  customers as customersAPI,
  clusters as clustersAPI,
  regions as regionsAPI,
  users as usersAPI,
  teams as teamsAPI,
  projects as projectsAPI,
  staffAssignments as staffAssignmentsAPI,
  notifications as notificationsAPI,
  surveys as surveysAPI,
  civilWork as civilWorkAPI,
  technicalWork as technicalWorkAPI,
  commissioningWork as commissioningWorkAPI,
  transportWork as transportWorkAPI,
} from "../../lib/database";
import { useSearchParams, useNavigate } from "react-router-dom";

const CustomerDatabase = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const CUSTOMER_DB_LAST_QUERY_KEY = "customerDatabase:lastQuery";
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [clusters, setClusters] = useState([]);
  const [regions, setRegions] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    searchType: "general", // "general" or "bulk_usn"
    bulkServiceNumbers: "",
    cluster: "all",
    region: [], // multi-select: array of region ids
    status: [], // multi-select: workflow status values; empty = all
    team: "all",
    teamMember: "all",
    quickFilter: "all",
    surveyCompletedOn: "",
  });
  // Applied filters: used for API fetch and URL. Updated when user clicks "Search" (or Clear).
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    searchType: "general",
    bulkServiceNumbers: "",
    cluster: "all",
    region: [],
    status: [],
    team: "all",
    teamMember: "all",
    quickFilter: "all",
    surveyCompletedOn: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isGenerateQRModalOpen, setIsGenerateQRModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [roleAssignModal, setRoleAssignModal] = useState({
    open: false,
    customer: null,
    role: null, // "civil" | "technical"
    memberId: "",
    saving: false,
  });
  const [statusOverrideModal, setStatusOverrideModal] = useState({
    open: false,
    customer: null,
    newStatus: "",
    saving: false,
  });
  const [stats, setStats] = useState({
    totalCustomers: 0,
    surveyComplete: 0,
    transportComplete: 0,
    civilComplete: 0,
    technicalComplete: 0,
    commissioningComplete: 0,
    unworkable: 0,
  });

  const mergeUsersById = (...lists) => {
    const byId = new Map();
    for (const list of lists) {
      for (const u of list || []) {
        if (u?.id) byId.set(u.id, u);
      }
    }
    return [...byId.values()];
  };

  const getWcrUsers = async ({ technicalWorkData, commissioningData }) => {
    const ids = [
      technicalWorkData?.submitted_by,
      commissioningData?.submitted_by,
    ].filter(Boolean);

    const extraUsers =
      ids.length > 0 ? await usersAPI.getByIds(ids).catch(() => []) : [];

    return mergeUsersById(teamMembers, extraUsers);
  };

  const DEFAULT_FILTERS = useMemo(
    () => ({
      search: "",
      searchType: "general", // "general" or "bulk_usn"
      bulkServiceNumbers: "",
      cluster: "all",
      region: [],
      status: [],
      team: "all",
      teamMember: "all",
      quickFilter: "all",
      surveyCompletedOn: "",
    }),
    [],
  );

  const hasInitializedFromUrlRef = useRef(false);

  const readFiltersFromUrl = (params = searchParams) => {
    const next = { ...DEFAULT_FILTERS };
    for (const key of Object.keys(DEFAULT_FILTERS)) {
      const v = params.get(key);
      if (v === null || v === undefined) continue;
      if (key === "region") {
        next[key] =
          v === "" || v === "all"
            ? []
            : v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
      } else if (key === "status") {
        next[key] =
          v === "" || v === "all"
            ? []
            : v
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
      } else if (v !== "") next[key] = v;
    }
    return next;
  };

  const readPaginationFromUrl = (params = searchParams) => {
    const pageRaw = params.get("page");
    const pageSizeRaw = params.get("pageSize");
    const page = pageRaw ? Number(pageRaw) : 1;
    const pageSize = pageSizeRaw ? Number(pageSizeRaw) : 10;
    return {
      page: Number.isFinite(page) && page >= 1 ? page : 1,
      pageSize:
        Number.isFinite(pageSize) && pageSize >= 1 && pageSize <= 1000
          ? pageSize
          : 10,
    };
  };

  const writeStateToUrl = (nextFilters, nextPage, nextPageSize) => {
    const params = {};

    // Write only non-default filter values to keep URL clean.
    Object.keys(DEFAULT_FILTERS).forEach((k) => {
      const v = nextFilters?.[k];
      if (v === undefined || v === null) return;
      if (k === "region") {
        if (!Array.isArray(v) || v.length === 0) return;
        params[k] = v.join(",");
        return;
      }
      if (k === "status") {
        if (!Array.isArray(v) || v.length === 0) return;
        params[k] = v.join(",");
        return;
      }
      if (v === DEFAULT_FILTERS[k]) return;
      if (typeof v === "string" && v.trim() === "") return;
      params[k] = String(v);
    });

    if (nextPage && nextPage !== 1) params.page = String(nextPage);
    if (nextPageSize && nextPageSize !== 10)
      params.pageSize = String(nextPageSize);

    // Persist last-used query so clicking the nav again can restore filters
    // even when landing on `/customer-database` with no search params.
    try {
      const nextQuery = new URLSearchParams(params).toString();
      localStorage.setItem(CUSTOMER_DB_LAST_QUERY_KEY, nextQuery);
    } catch {
      // ignore storage errors (private mode / disabled storage)
    }

    setSearchParams(params, { replace: true });
  };

  const STATUS_LABELS = {
    survey_not_started: "Survey Not Started",
    survey_inprogress: "Survey In Progress",
    survey_complete: "Survey Complete",
    survey_unworkable: "Survey Unworkable",
    transport_pending: "Transport Pending",
    transport_inprogress: "Transport In Progress",
    transport_complete: "Transport Complete",
    civil_pending: "Civil Pending",
    civil_inprogress: "Civil In Progress",
    civil_complete: "Civil Complete",
    technical_pending: "Technical Pending",
    technical_inprogress: "Technical In Progress",
    technical_complete: "Technical Complete",
    commissioning_not_started: "Commissioning Not Started",
    commissioning_inprogress: "Commissioning In Progress",
    commissioning_complete: "Commissioning Complete",
  };

  const canOverrideStatus =
    user?.role === "super_admin" || user?.role === "cluster_manager";

  // Status hierarchy constant
  const STATUS_HIERARCHY = [
    "survey_not_started",
    "survey_inprogress",
    "survey_unworkable",
    "survey_complete",
    "transport_pending",
    "transport_inprogress",
    "transport_complete",
    "civil_pending",
    "civil_inprogress",
    "civil_complete",
    "technical_pending",
    "technical_inprogress",
    "technical_complete",
    "commissioning_not_started",
    "commissioning_inprogress",
    "commissioning_complete",
  ];

  // Helper function to get statuses from selected status onwards
  const getStatusesFrom = (selectedStatus) => {
    if (!selectedStatus || selectedStatus === "all") return null;
    const index = STATUS_HIERARCHY.indexOf(selectedStatus);
    if (index === -1) {
      console.warn(
        `Status "${selectedStatus}" not found in hierarchy, using single status`,
      );
      return [selectedStatus];
    }
    return STATUS_HIERARCHY.slice(index);
  };

  // Listen for project selection changes
  useEffect(() => {
    const updateSelectedProject = () => {
      const project = localStorage.getItem("selectedProject");
      setSelectedProject(project && project !== "all" ? project : null);
    };

    // Initial load
    updateSelectedProject();

    // Listen for storage changes (when project is selected in another tab/window)
    const handleStorageChange = (e) => {
      if (e.key === "selectedProject") {
        updateSelectedProject();
      }
    };

    // Listen for custom event (when project is selected in same tab)
    const handleProjectChange = () => {
      updateSelectedProject();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("projectChanged", handleProjectChange);

    // Poll localStorage periodically to catch changes in same tab
    const interval = setInterval(updateSelectedProject, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("projectChanged", handleProjectChange);
      clearInterval(interval);
    };
  }, []);

  // Initialize filters/pagination from URL on first mount (and when landing back).
  useEffect(() => {
    if (hasInitializedFromUrlRef.current) return;
    let paramsToRead = searchParams;

    // If user clicks "Customers" nav again, route is usually `/customer-database`
    // with no query string. Restore from localStorage in that case.
    if (!searchParams?.toString()) {
      try {
        const savedQuery = localStorage.getItem(CUSTOMER_DB_LAST_QUERY_KEY);
        if (savedQuery) {
          paramsToRead = new URLSearchParams(savedQuery);
          setSearchParams(savedQuery, { replace: true });
        }
      } catch {
        // ignore storage errors
      }
    }

    const nextFilters = readFiltersFromUrl(paramsToRead);
    const { page, pageSize } = readPaginationFromUrl(paramsToRead);

    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setCurrentPage(page);
    setItemsPerPage(pageSize);
    hasInitializedFromUrlRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with applied state (after initialization).
  useEffect(() => {
    if (!hasInitializedFromUrlRef.current) return;
    writeStateToUrl(appliedFilters, currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, currentPage, itemsPerPage]);

  // Fetch data when applied filters, pagination, or project changes (not on every keystroke)
  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user, selectedProject, currentPage, itemsPerPage, appliedFilters]);

  // Fetch stats only when user or project changes (not filters)
  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user, selectedProject]);

  // Helper to get base filters based on user role
  const getBaseFilters = () => {
    const baseFilters = {};

    if (user?.role === "cluster_manager" && user?.cluster_id) {
      baseFilters.cluster_id = user.cluster_id;
    } else if (user?.role === "project_admin" && user?.project_id) {
      const selectedProject =
        localStorage.getItem("selectedProject") || user.project_id;
      baseFilters.project_id = selectedProject;
    } else if (user?.role === "super_admin") {
      const selectedProject = localStorage.getItem("selectedProject");
      if (selectedProject && selectedProject !== "all") {
        baseFilters.project_id = selectedProject;
      }
    }

    return baseFilters;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Handle bulk CIN/USN search separately
      if (
        appliedFilters.searchType === "bulk_usn" &&
        appliedFilters.bulkServiceNumbers?.trim()
      ) {
        // Parse comma-separated CINs or USNs
        const identifiers = appliedFilters.bulkServiceNumbers
          .split(",")
          .map((id) => id.trim())
          .filter((id) => id.length > 0);

        if (identifiers.length > 0) {
          // Restrict bulk search to selected project (super_admin/project_admin) or cluster (cluster_manager)
          const bulkOptions = {};
          if (user?.role === "cluster_manager" && user?.cluster_id) {
            bulkOptions.clusterId = user.cluster_id;
          } else if (user?.role === "project_admin" && user?.project_id) {
            const selectedProject =
              localStorage.getItem("selectedProject") || user.project_id;
            if (selectedProject) bulkOptions.projectId = selectedProject;
          } else if (user?.role === "super_admin") {
            const selectedProject = localStorage.getItem("selectedProject");
            if (selectedProject && selectedProject !== "all") {
              bulkOptions.projectId = selectedProject;
            }
          }
          const customersData = await customersAPI.getByMultipleCINsOrUSNs(
            identifiers,
            bulkOptions,
          );

          // Fetch supporting data scoped by role/project/cluster so filters & table
          // only show relevant clusters, teams and team members.
          let clustersData = [];
          let regionsData = [];
          let teamsData = [];
          let projectsData = [];
          let staffUsers = [];

          if (user?.role === "cluster_manager" && user?.cluster_id) {
            // Cluster manager: only their cluster, its project's regions/teams and staff in that cluster
            const cluster = await clustersAPI
              .getById(user.cluster_id)
              .catch(() => null);
            clustersData = cluster ? [cluster] : [];

            if (cluster?.project_id) {
              const [regionsForProject, teamsForCluster, clusterUsers] =
                await Promise.all([
                  regionsAPI.getByProjectId(cluster.project_id).catch(() => []),
                  teamsAPI.getByClusterId(user.cluster_id).catch(() => []),
                  usersAPI.getByClusterId(user.cluster_id).catch(() => []),
                ]);
              regionsData = regionsForProject || [];
              teamsData = teamsForCluster || [];
              staffUsers = (clusterUsers || []).filter(
                (u) => u.category === "staff",
              );
            }

            projectsData = await projectsAPI.getAll().catch(() => []);
          } else if (user?.role === "project_admin" && user?.project_id) {
            // Project admin: scope to their project / selected project
            const selectedProjectId =
              localStorage.getItem("selectedProject") || user.project_id;
            const [
              clustersForProject,
              regionsForProject,
              teamsForProject,
              allProjects,
              projectUsers,
            ] = await Promise.all([
              clustersAPI.getByProjectId(selectedProjectId).catch(() => []),
              regionsAPI.getByProjectId(selectedProjectId).catch(() => []),
              teamsAPI.getByProjectId(selectedProjectId).catch(() => []),
              projectsAPI.getAll().catch(() => []),
              usersAPI.getByProjectId(selectedProjectId).catch(() => []),
            ]);
            clustersData = clustersForProject || [];
            regionsData = regionsForProject || [];
            teamsData = teamsForProject || [];
            projectsData = allProjects || [];
            staffUsers = (projectUsers || []).filter(
              (u) => u.category === "staff",
            );
          } else if (user?.role === "super_admin") {
            // Super admin: when a project is selected, scope to that project
            const selectedProjectId = localStorage.getItem("selectedProject");
            if (selectedProjectId && selectedProjectId !== "all") {
              const [
                clustersForProject,
                regionsForProject,
                teamsForProject,
                allProjects,
                projectUsers,
              ] = await Promise.all([
                clustersAPI.getByProjectId(selectedProjectId).catch(() => []),
                regionsAPI.getByProjectId(selectedProjectId).catch(() => []),
                teamsAPI.getByProjectId(selectedProjectId).catch(() => []),
                projectsAPI.getAll().catch(() => []),
                usersAPI.getByProjectId(selectedProjectId).catch(() => []),
              ]);
              clustersData = clustersForProject || [];
              regionsData = regionsForProject || [];
              teamsData = teamsForProject || [];
              projectsData = allProjects || [];
              staffUsers = (projectUsers || []).filter(
                (u) => u.category === "staff",
              );
            } else {
              const [allClusters, allRegions, allTeams, allProjects, allUsers] =
                await Promise.all([
                  clustersAPI.getAll().catch(() => []),
                  regionsAPI.getAll().catch(() => []),
                  teamsAPI.getAll().catch(() => []),
                  projectsAPI.getAll().catch(() => []),
                  usersAPI.getAll().catch(() => []),
                ]);
              clustersData = allClusters || [];
              regionsData = allRegions || [];
              teamsData = allTeams || [];
              projectsData = allProjects || [];
              staffUsers = (allUsers || []).filter(
                (u) => u.category === "staff",
              );
            }
          } else {
            // Fallback: show everything
            const [allClusters, allRegions, allTeams, allProjects, allUsers] =
              await Promise.all([
                clustersAPI.getAll().catch(() => []),
                regionsAPI.getAll().catch(() => []),
                teamsAPI.getAll().catch(() => []),
                projectsAPI.getAll().catch(() => []),
                usersAPI.getAll().catch(() => []),
              ]);
            clustersData = allClusters || [];
            regionsData = allRegions || [];
            teamsData = allTeams || [];
            projectsData = allProjects || [];
            staffUsers = (allUsers || []).filter((u) => u.category === "staff");
          }

          // Enrich customers with assignment data and survey/civil approval status
          if (customersData && customersData.length > 0) {
            const customerIds = customersData.map((c) => c.id);
            try {
              const [allAssignments, surveyApprovals, civilApprovals, technicalApprovals] =
                await Promise.all([
                  staffAssignmentsAPI
                    .getByCustomerId(customerIds)
                    .catch(() => []),
                  surveysAPI
                    .getApprovalStatusByCustomerIds(customerIds)
                    .catch(() => []),
                  civilWorkAPI
                    .getApprovalStatusByCustomerIds(customerIds)
                    .catch(() => []),
                  technicalWorkAPI
                    .getApprovalStatusByCustomerIds(customerIds)
                    .catch(() => []),
                ]);

              const surveyByCustomer = (surveyApprovals || []).reduce(
                (acc, row) => {
                  acc[row.customer_id] = row.approval_status;
                  return acc;
                },
                {},
              );
              const civilByCustomer = (civilApprovals || []).reduce(
                (acc, row) => {
                  acc[row.customer_id] = row.approval_status;
                  return acc;
                },
                {},
              );
              const technicalByCustomer = (technicalApprovals || []).reduce(
                (acc, row) => {
                  acc[row.customer_id] = row.approval_status;
                  return acc;
                },
                {},
              );

              const enrichedCustomers = customersData.map((customer) => {
                const assignment = (allAssignments || []).find(
                  (a) => a.customer_id === customer.id,
                );
                return {
                  ...customer,
                  assigned_member_id: assignment?.user_id || null,
                  assignment: assignment,
                  survey_approval_status: surveyByCustomer[customer.id] ?? null,
                  civil_approval_status: civilByCustomer[customer.id] ?? null,
                  technical_approval_status:
                    technicalByCustomer[customer.id] ?? null,
                };
              });

              setCustomers(enrichedCustomers);
              setTotalCustomers(enrichedCustomers.length);
            } catch (err) {
              console.error("Error enriching customers:", err);
              setCustomers(
                customersData.map((c) => ({ ...c, assigned_member_id: null })),
              );
              setTotalCustomers(customersData.length);
            }
          } else {
            setCustomers([]);
            setTotalCustomers(0);
          }

          setTeams(teamsData || []);
          setTeamMembers(staffUsers);
          setClusters(clustersData || []);
          setRegions(regionsData || []);
          setProjects(projectsData || []);
          setLoading(false);
          return;
        } else {
          // No valid CINs/USNs entered
          setCustomers([]);
          setTotalCustomers(0);
          setLoading(false);
          return;
        }
      }

      // Build filters for API calls (normal search)
      const apiFilters = {};
      if (appliedFilters.cluster !== "all") {
        apiFilters.cluster_id = appliedFilters.cluster;
      }
      if (appliedFilters.region?.length) {
        apiFilters.region_id = appliedFilters.region;
      }
      // Apply quick filter status logic first (takes precedence over status filter)
      if (appliedFilters.quickFilter === "completed_surveys") {
        // Survey complete and above
        apiFilters.status = getStatusesFrom("survey_complete");
      } else if (appliedFilters.quickFilter === "completed_civil") {
        // Civil complete and above
        apiFilters.status = getStatusesFrom("civil_complete");
      } else if (appliedFilters.quickFilter === "completed_technical") {
        // Technical complete
        apiFilters.status = getStatusesFrom("technical_complete");
      } else if (appliedFilters.quickFilter === "completed_commissioning") {
        // Commissioning complete
        apiFilters.status = getStatusesFrom("commissioning_complete");
      } else if (
        Array.isArray(appliedFilters.status) &&
        appliedFilters.status.length > 0
      ) {
        apiFilters.status = appliedFilters.status;
      } else if (
        typeof appliedFilters.status === "string" &&
        appliedFilters.status !== "all"
      ) {
        apiFilters.status = appliedFilters.status;
      }

      if (appliedFilters.team !== "all") {
        apiFilters.team_id = appliedFilters.team;
      }
      if (appliedFilters.search) {
        apiFilters.search = appliedFilters.search;
      }
      if (appliedFilters.surveyCompletedOn) {
        const v = appliedFilters.surveyCompletedOn.trim();
        if (v) {
          const dateStr = v.includes("T") ? v.slice(0, 10) : v;
          const timeStr = v.includes("T") ? v.slice(11, 19) : "";
          const isMidnight =
            !timeStr || timeStr === "00:00" || timeStr.startsWith("00:00:");
          // Range: start of selected day (local) → chosen time (local); convert to UTC for DB (submitted_at is timestamptz)
          const fromLocal = new Date(dateStr + "T00:00:00");
          const toLocal = isMidnight
            ? new Date(dateStr + "T23:59:59.999")
            : new Date(v);
          if (
            !Number.isNaN(fromLocal.getTime()) &&
            !Number.isNaN(toLocal.getTime())
          ) {
            const completionPhaseByQuickFilter =
              appliedFilters.quickFilter === "completed_surveys"
                ? "survey"
                : appliedFilters.quickFilter === "completed_civil"
                  ? "civil"
                  : appliedFilters.quickFilter === "completed_technical"
                    ? "technical"
                    : appliedFilters.quickFilter === "completed_commissioning"
                      ? "commissioning"
                      : null;
            if (completionPhaseByQuickFilter) {
              apiFilters.phase_completed_from = fromLocal.toISOString();
              apiFilters.phase_completed_to = toLocal.toISOString();
              apiFilters.completion_phase = completionPhaseByQuickFilter;
            }
          }
        }
      }

      // Use server-side pagination (quick filters are now handled server-side)
      const usePagination = true;
      const effectivePage = usePagination ? currentPage : 1;
      const effectivePageSize = usePagination ? itemsPerPage : 999999;

      // Fetch data based on user role
      let customersData, clustersData, regionsData, teamsData, totalCount;

      if (user?.role === "cluster_manager" && user?.cluster_id) {
        // Cluster manager: only see their cluster's data
        const countFilters = { ...apiFilters, cluster_id: user.cluster_id };
        [totalCount, clustersData, teamsData] = await Promise.all([
          customersAPI.getCount(countFilters),
          clustersAPI
            .getById(user.cluster_id)
            .then((c) => [c])
            .catch(() => []),
          teamsAPI.getByClusterId(user.cluster_id).catch(() => []),
        ]);

        customersData = await customersAPI.getByClusterId(user.cluster_id, {
          page: effectivePage,
          pageSize: effectivePageSize,
          filters: apiFilters,
        });

        // Get regions for this cluster's project
        const cluster = clustersData?.[0];
        if (cluster?.project_id) {
          regionsData = await regionsAPI
            .getByProjectId(cluster.project_id)
            .catch(() => []);
        } else {
          regionsData = [];
        }
      } else if (user?.role === "project_admin" && user?.project_id) {
        // Project admin: see their project's data
        const selectedProject =
          localStorage.getItem("selectedProject") || user.project_id;
        const countFilters = { ...apiFilters, project_id: selectedProject };
        [totalCount, clustersData, regionsData, teamsData] = await Promise.all([
          customersAPI.getCount(countFilters),
          clustersAPI.getByProjectId(selectedProject).catch(() => []),
          regionsAPI.getByProjectId(selectedProject).catch(() => []),
          teamsAPI.getByProjectId(selectedProject).catch(() => []),
        ]);

        customersData = await customersAPI.getByProjectId(selectedProject, {
          page: effectivePage,
          pageSize: effectivePageSize,
          filters: apiFilters,
        });
      } else if (user?.role === "super_admin") {
        // Super admin: see all data, optionally filtered by selected project
        const selectedProject = localStorage.getItem("selectedProject");
        if (selectedProject && selectedProject !== "all") {
          const countFilters = { ...apiFilters, project_id: selectedProject };
          [totalCount, clustersData, regionsData, teamsData] =
            await Promise.all([
              customersAPI.getCount(countFilters),
              clustersAPI.getByProjectId(selectedProject).catch(() => []),
              regionsAPI.getByProjectId(selectedProject).catch(() => []),
              teamsAPI.getByProjectId(selectedProject).catch(() => []),
            ]);

          customersData = await customersAPI.getByProjectId(selectedProject, {
            page: effectivePage,
            pageSize: effectivePageSize,
            filters: apiFilters,
          });
        } else {
          [totalCount, clustersData, regionsData, teamsData] =
            await Promise.all([
              customersAPI.getCount(apiFilters),
              clustersAPI.getAll().catch(() => []),
              regionsAPI.getAll().catch(() => []),
              teamsAPI.getAll().catch(() => []),
            ]);

          customersData = await customersAPI.getAll({
            page: effectivePage,
            pageSize: effectivePageSize,
            filters: apiFilters,
          });
        }
      } else {
        // Default: fetch all (for other roles)
        [totalCount, clustersData, regionsData, teamsData] = await Promise.all([
          customersAPI.getCount(apiFilters),
          clustersAPI.getAll().catch(() => []),
          regionsAPI.getAll().catch(() => []),
          teamsAPI.getAll().catch(() => []),
        ]);

        customersData = await customersAPI.getAll({
          page: effectivePage,
          pageSize: effectivePageSize,
          filters: apiFilters,
        });
      }

      const [projectsData] = await Promise.all([
        projectsAPI.getAll().catch(() => []),
      ]);

      // Get staff users for team member selection based on role
      let staffUsers = [];
      if (user?.role === "cluster_manager" && user?.cluster_id) {
        // Cluster manager: only see team members of their cluster
        const clusterUsers = await usersAPI
          .getByClusterId(user.cluster_id)
          .catch(() => []);
        staffUsers = (clusterUsers || []).filter(
          (user) => user.category === "staff",
        );
      } else if (user?.role === "project_admin" && user?.project_id) {
        // Project admin: see all team members of the project
        const selectedProject =
          localStorage.getItem("selectedProject") || user.project_id;
        const projectUsers = await usersAPI
          .getByProjectId(selectedProject)
          .catch(() => []);
        staffUsers = (projectUsers || []).filter(
          (user) => user.category === "staff",
        );
      } else if (user?.role === "super_admin") {
        // Super admin: see all team members, optionally filtered by selected project
        const selectedProject = localStorage.getItem("selectedProject");
        if (selectedProject && selectedProject !== "all") {
          const projectUsers = await usersAPI
            .getByProjectId(selectedProject)
            .catch(() => []);
          staffUsers = (projectUsers || []).filter(
            (user) => user.category === "staff",
          );
        } else {
          const allUsers = await usersAPI.getAll().catch(() => []);
          staffUsers = (allUsers || []).filter(
            (user) => user.category === "staff",
          );
        }
      } else {
        // Default: get all staff users
        const allUsers = await usersAPI.getAll().catch(() => []);
        staffUsers = (allUsers || []).filter(
          (user) => user.category === "staff",
        );
      }

      // Set total count
      setTotalCustomers(totalCount || 0);

      // Enrich customers with assignment data and survey/civil approval status
      if (customersData && customersData.length > 0) {
        const customerIds = customersData.map((c) => c.id);

        const [allAssignments, surveyApprovals, civilApprovals, technicalApprovals] =
          await Promise.all([
            staffAssignmentsAPI.getByCustomerId(customerIds).catch(() => []),
            surveysAPI
              .getApprovalStatusByCustomerIds(customerIds)
              .catch(() => []),
            civilWorkAPI
              .getApprovalStatusByCustomerIds(customerIds)
              .catch(() => []),
            technicalWorkAPI
              .getApprovalStatusByCustomerIds(customerIds)
              .catch(() => []),
          ]);

        const surveyByCustomer = (surveyApprovals || []).reduce((acc, row) => {
          acc[row.customer_id] = row.approval_status;
          return acc;
        }, {});
        const civilByCustomer = (civilApprovals || []).reduce((acc, row) => {
          acc[row.customer_id] = row.approval_status;
          return acc;
        }, {});
        const technicalByCustomer = (technicalApprovals || []).reduce(
          (acc, row) => {
            acc[row.customer_id] = row.approval_status;
            return acc;
          },
          {},
        );

        const enrichedCustomers = customersData.map((customer) => {
          const assignment = (allAssignments || []).find(
            (a) => a.customer_id === customer.id,
          );
          return {
            ...customer,
            assigned_member_id: assignment?.user_id || null,
            assignment: assignment,
            survey_approval_status: surveyByCustomer[customer.id] ?? null,
            civil_approval_status: civilByCustomer[customer.id] ?? null,
            technical_approval_status:
              technicalByCustomer[customer.id] ?? null,
          };
        });

        setCustomers(enrichedCustomers);
      } else {
        setCustomers([]);
      }

      setTeams(teamsData || []);
      setTeamMembers(staffUsers);
      setClusters(clustersData || []);
      setRegions(regionsData || []);
      setProjects(projectsData || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats directly from database
  const fetchStats = async () => {
    try {
      const baseFilters = getBaseFilters();

      // Fetch global stats counts (no filters applied, only base role/project filters)
      const [
        totalCustomersCount,
        surveyCompleteCount,
        transportCompleteCount,
        civilCompleteCount,
        technicalCompleteCount,
        commissioningCompleteCount,
        unworkableCount,
      ] = await Promise.all([
        // Total Customers: All customers (only base filters for role/project)
        customersAPI.getCount(baseFilters),
        // Survey Complete+: survey_complete and all later pipeline statuses
        customersAPI.getCount({
          ...baseFilters,
          status: getStatusesFrom("survey_complete"),
        }),
        // Transport Complete+: transport_complete and all later statuses
        customersAPI.getCount({
          ...baseFilters,
          status: getStatusesFrom("transport_complete"),
        }),
        // Civil Complete+: civil_complete and all later statuses
        customersAPI.getCount({
          ...baseFilters,
          status: getStatusesFrom("civil_complete"),
        }),
        // Technical Complete+: technical_complete and commissioning stages
        customersAPI.getCount({
          ...baseFilters,
          status: getStatusesFrom("technical_complete"),
        }),
        // Commissioning Complete: post-technical commissioning done
        customersAPI.getCount({
          ...baseFilters,
          status: "commissioning_complete",
        }),
        // Unworkable: surveys marked not acceptable
        customersAPI.getCount({
          ...baseFilters,
          status: "survey_unworkable",
        }),
      ]);

      setStats({
        totalCustomers: totalCustomersCount || 0,
        surveyComplete: surveyCompleteCount || 0,
        transportComplete: transportCompleteCount || 0,
        civilComplete: civilCompleteCount || 0,
        technicalComplete: technicalCompleteCount || 0,
        commissioningComplete: commissioningCompleteCount || 0,
        unworkable: unworkableCount || 0,
      });
    } catch (err) {
      console.error("Error fetching stats:", err);
      // Set stats to 0 on error
      setStats({
        totalCustomers: 0,
        surveyComplete: 0,
        transportComplete: 0,
        civilComplete: 0,
        technicalComplete: 0,
        commissioningComplete: 0,
        unworkable: 0,
      });
    }
  };

  // Apply quick filters and team member filter client-side after fetching customers
  useEffect(() => {
    let filtered = [...customers];

    // Apply team member filter
    if (appliedFilters.teamMember !== "all") {
      if (appliedFilters.teamMember === "not_assigned") {
        filtered = filtered.filter((customer) => !customer.assigned_member_id);
      } else {
        filtered = filtered.filter(
          (customer) =>
            customer.assigned_member_id === appliedFilters.teamMember,
        );
      }
    }

    // Quick filters are now handled at API level, no client-side filtering needed

    setFilteredCustomers(filtered);
    setSelectedIds([]);
  }, [customers, appliedFilters.quickFilter, appliedFilters.teamMember]);

  const handleSearch = (nextFilters = filters) => {
    setAppliedFilters(nextFilters);
    setCurrentPage(1);
  };

  const handleClearFiltersAndSearch = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleAddCustomer = async (customerData) => {
    // Convert empty strings to null for UUID fields and other optional fields
    const cleanedData = {
      ...customerData,
      aadhaar_number: customerData.aadhaar_number || null,
      dob: customerData.dob || null,
      address: customerData.address || null,
      village: customerData.village || null,
      district: customerData.district || null,
      state: customerData.state || null,
      pincode: customerData.pincode || null,
      cluster_id: customerData.cluster_id || null,
    };
    const createdCustomer = await customersAPI.create(cleanedData);
    await fetchData(); // Refresh data
    await fetchStats(); // Refresh stats
    setIsAddModalOpen(false);
  };

  const handleEdit = (customer) => {
    if (!customer?.id) return;
    navigate(`/customer-detail/${customer.id}`);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this customer? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      setError(null);
      setDeletingId(customerId);
      await customersAPI.delete(customerId);
      await fetchData(); // Refresh data
      await fetchStats(); // Refresh stats
    } catch (err) {
      console.error("Error deleting customer:", err);
      setError(err.message || "Failed to delete customer");
    } finally {
      setDeletingId(null);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      showToast("Please select at least one customer", "error");
      return;
    }
    if (
      !window.confirm(
        `Are you sure you want to delete ${selectedIds.length} selected customer(s)? This action cannot be undone.`,
      )
    ) {
      return;
    }
    const count = selectedIds.length;
    try {
      setError(null);
      setIsBulkDeleting(true);
      await Promise.all(selectedIds.map((id) => customersAPI.delete(id)));
      setSelectedIds([]);
      await fetchData();
      await fetchStats();
      showToast(`${count} customer(s) deleted successfully`, "success");
    } catch (err) {
      console.error("Error bulk deleting customers:", err);
      showToast(err.message || "Failed to delete some customers", "error");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleAssignTeam = async (customerId, teamId) => {
    try {
      setError(null);

      await customersAPI.update(customerId, {
        team_id: teamId || null,
      });

      // If "No team" is assigned (teamId is null/empty), remove all staff assignments
      if (!teamId) {
        await staffAssignmentsAPI.deleteByCustomer(customerId);
      }

      await fetchData(); // Refresh data
    } catch (err) {
      console.error("Error assigning team:", err);
      setError(err.message || "Failed to assign team");
      throw err;
    }
  };

  const handleAssignMember = async (customerId, memberId) => {
    try {
      setError(null);

      // Get customer and member names for toast message
      const customer = customers.find((c) => c.id === customerId);
      const member = teamMembers.find((m) => m.id === memberId);

      if (memberId) {
        if (member?.role !== "worker") {
          showToast("Only worker role can be assigned.", "error");
          return;
        }

        // First, update the customer's team_id to match the member's team
        if (member?.team_id) {
          await customersAPI.update(customerId, {
            team_id: member.team_id,
          });
        }

        // Check if there's a non-completed assignment
        const existingAssignments =
          await staffAssignmentsAPI.getByCustomerId(customerId);
        const nonCompletedAssignment = (existingAssignments || [])?.find(
          (assignment) => assignment.status !== "completed",
        );

        if (nonCompletedAssignment) {
          // Update the existing non-completed assignment to the new member
          await staffAssignmentsAPI.update(nonCompletedAssignment.id, {
            user_id: memberId,
            status: "assigned",
            assigned_at: new Date().toISOString(),
            completed_at: null, // Clear completed_at if it was set
          });

          // Create notification for the reassigned user
          try {
            await notificationsAPI.notifyUserOnAssignment(memberId, customerId);
          } catch (notifError) {
            console.error(
              "Error creating assignment notification:",
              notifError,
            );
            // Don't block the assignment if notification fails
          }

          // Show success toast
          showToast(
            `Customer "${customer?.name || "Unknown"}" reassigned to ${
              member?.name || "member"
            } successfully`,
            "success",
          );
        } else {
          // All assignments are completed (or none exist), create a new assignment
          await staffAssignmentsAPI.create(customerId, memberId);

          // Create notification for the assigned user
          try {
            await notificationsAPI.notifyUserOnAssignment(memberId, customerId);
          } catch (notifError) {
            console.error(
              "Error creating assignment notification:",
              notifError,
            );
            // Don't block the assignment if notification fails
          }

          // Show success toast
          showToast(
            `Customer "${customer?.name || "Unknown"}" assigned to ${
              member?.name || "member"
            } successfully`,
            "success",
          );
        }

        // Assignment does not auto-change phase status in single-worker model.
      } else {
        // If memberId is null, remove assignment
        await staffAssignmentsAPI.deleteByCustomer(customerId);

        // Show info toast
        showToast(
          `Assignment removed for customer "${customer?.name || "Unknown"}"`,
          "info",
        );
      }

      await fetchData(); // Refresh data
    } catch (err) {
      console.error("Error assigning member:", err);
      setError(err.message || "Failed to assign member");
      showToast(
        `Failed to assign member: ${err.message || "Unknown error"}`,
        "error",
      );
      throw err;
    }
  };

  const handleAssignClusterToCustomer = async (customerId, clusterId) => {
    try {
      setError(null);

      await customersAPI.update(customerId, {
        cluster_id: clusterId || null,
      });
      await fetchData(); // Refresh data
    } catch (err) {
      console.error("Error assigning cluster:", err);
      setError(err.message || "Failed to assign cluster");
      throw err;
    }
  };

  // Progressive status rules: survey → transport → civil → technical → commissioning
  const isStatusAllowedForCustomer = (customer, newStatus) => {
    const currentStatus = customer?.status;
    const surveyApproved =
      (customer?.survey_approval_status || "").toLowerCase() === "approved";
    const civilApproved =
      (customer?.civil_approval_status || "").toLowerCase() === "approved";
    const technicalApproved =
      (customer?.technical_approval_status || "").toLowerCase() === "approved";

    const transportStatuses = [
      "transport_pending",
      "transport_inprogress",
      "transport_complete",
    ];
    const civilStatuses = [
      "civil_pending",
      "civil_inprogress",
      "civil_complete",
    ];
    const technicalStatuses = [
      "technical_pending",
      "technical_inprogress",
      "technical_complete",
    ];
    const commissioningStatuses = [
      "commissioning_not_started",
      "commissioning_inprogress",
      "commissioning_complete",
    ];

    const surveyCompleteOrLater = [
      "survey_complete",
      ...transportStatuses,
      ...civilStatuses,
      ...technicalStatuses,
      ...commissioningStatuses,
    ];
    const civilCompleteOrLater = [
      "civil_complete",
      ...technicalStatuses,
      ...commissioningStatuses,
    ];

    let allowed = [
      "survey_not_started",
      "survey_inprogress",
      "survey_complete",
    ];
    if (currentStatus === "survey_unworkable") {
      allowed.push("survey_unworkable");
    }

    if (surveyCompleteOrLater.includes(currentStatus)) {
      allowed = [
        "survey_not_started",
        "survey_inprogress",
        "survey_complete",
        ...transportStatuses,
        ...civilStatuses,
      ];
      if (!surveyApproved) {
        allowed = allowed.filter((s) => !civilStatuses.includes(s));
      }
    }
    if (civilCompleteOrLater.includes(currentStatus)) {
      allowed = [
        "survey_not_started",
        "survey_inprogress",
        "survey_complete",
        ...transportStatuses,
        ...civilStatuses,
        ...technicalStatuses,
        ...commissioningStatuses,
      ];
      if (!surveyApproved) {
        allowed = allowed.filter((s) => !civilStatuses.includes(s));
      }
      if (!civilApproved) {
        allowed = allowed.filter((s) => !technicalStatuses.includes(s));
      }
      const inCommissioningPhase = commissioningStatuses.includes(currentStatus);
      if (!technicalApproved && !inCommissioningPhase) {
        allowed = allowed.filter((s) => !commissioningStatuses.includes(s));
      }
    }
    return allowed.includes(newStatus);
  };

  const getStatusLabel = (status) =>
    STATUS_LABELS[status] || status || "—";

  const applyCustomerStatusUpdate = async (customerId, status) => {
    await customersAPI.update(customerId, {
      status: status || null,
    });

    await fetchData();
    await fetchStats();
  };

  const openStatusOverrideModal = (customer, newStatus) => {
    setStatusOverrideModal({
      open: true,
      customer,
      newStatus,
      saving: false,
    });
  };

  const closeStatusOverrideModal = () => {
    if (statusOverrideModal.saving) return;
    setStatusOverrideModal({
      open: false,
      customer: null,
      newStatus: "",
      saving: false,
    });
  };

  const handleConfirmStatusOverride = async () => {
    const { customer, newStatus } = statusOverrideModal;
    if (!customer?.id || !newStatus) return;

    try {
      setStatusOverrideModal((prev) => ({ ...prev, saving: true }));
      setError(null);

      await applyCustomerStatusUpdate(customer.id, newStatus);

      showToast("Status override applied successfully.", "success");
      setStatusOverrideModal({
        open: false,
        customer: null,
        newStatus: "",
        saving: false,
      });
    } catch (err) {
      console.error("Error applying status override:", err);
      setError(err.message || "Failed to apply status override");
      showToast(err.message || "Failed to apply status override", "error");
      setStatusOverrideModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleAssignStatus = async (customerId, status) => {
    try {
      setError(null);

      const customer = customers.find((c) => c.id === customerId);
      if (!customer) return;
      if (status === customer.status) return;

      if (!isStatusAllowedForCustomer(customer, status)) {
        if (canOverrideStatus) {
          openStatusOverrideModal(customer, status);
          return;
        }
        showToast(
          "Status not allowed: follow survey → transport → civil → technical → commissioning order, with required approvals at each phase.",
          "error",
        );
        return;
      }

      await applyCustomerStatusUpdate(customerId, status);
    } catch (err) {
      console.error("Error updating status:", err);
      setError(err.message || "Failed to update status");
      throw err;
    }
  };

  const handleQuickAssignByRole = async (customerId, role) => {
    try {
      setError(null);

      // Get the customer to find their team_id
      const customer = customers.find((c) => c.id === customerId);
      if (!customer) {
        throw new Error("Customer not found");
      }

      if (!customer.team_id) {
        showToast("Customer must be assigned to a team first", "error");
        return;
      }

      // Find the first worker in the customer's team
      const teamMember = teamMembers.find(
        (member) =>
          member.team_id === customer.team_id && member.role === "worker",
      );

      if (!teamMember) {
        showToast("No worker found in this team", "error");
        return;
      }

      // Assign the customer to this team member
      await handleAssignMember(customerId, teamMember.id);
    } catch (err) {
      console.error("Error in quick assignment:", err);
      setError(err.message || "Failed to assign customer");
      showToast(
        `Failed to assign customer: ${err.message || "Unknown error"}`,
        "error",
      );
      throw err;
    }
  };

  const handleUpload = async (customersBatch) => {
    try {
      const allowedUpdateKeys = [
        "name",
        "phone_number",
        "project_id",
        "region_id",
        "cluster_id",
        "address",
        "village",
        "district",
        "state",
        "pincode",
        "aadhaar_number",
        "usn",
      ];
      for (const customer of customersBatch) {
        const usn = customer.usn != null ? String(customer.usn).trim() : "";
        if (usn) {
          const existing = await customersAPI.getByUsnMaybe(usn);
          if (existing) {
            const updates = {};
            allowedUpdateKeys.forEach((key) => {
              if (customer[key] !== undefined) updates[key] = customer[key];
            });
            await customersAPI.update(existing.id, updates);
            continue;
          }
        }
        await customersAPI.create(customer);
      }
    } catch (err) {
      console.error("Error uploading customers:", err);
      throw err;
    }
  };

  const runBulkMemberAssignment = async (memberId, options = {}) => {
    const { suppressToast = false } = options;
    const member = teamMembers.find((m) => m.id === memberId);
    if (memberId && !member) throw new Error("Member not found");

    if (memberId && member?.role !== "worker") {
      showToast("Only worker role can be assigned.", "error");
      throw new Error("Invalid member role");
    }

    const memberTeamId = member?.team_id;

    // No assignment caps: allow staff to be assigned to unlimited customers.

    const assignmentPromises = selectedIds.map(async (customerId) => {
      if (memberId) {
        if (memberTeamId) {
          await customersAPI.update(customerId, { team_id: memberTeamId });
        }
        const existingAssignments =
          await staffAssignmentsAPI.getByCustomerId(customerId);
        const nonCompletedAssignment = existingAssignments?.find(
          (a) => a.status !== "completed",
        );
        if (nonCompletedAssignment) {
          await staffAssignmentsAPI.update(nonCompletedAssignment.id, {
            user_id: memberId,
            status: "assigned",
            assigned_at: new Date().toISOString(),
            completed_at: null,
          });
          try {
            await notificationsAPI.notifyUserOnAssignment(memberId, customerId);
          } catch (notifError) {
            console.error(
              "Error creating assignment notification:",
              notifError,
            );
          }
        } else {
          await staffAssignmentsAPI.create(customerId, memberId);
          try {
            await notificationsAPI.notifyUserOnAssignment(memberId, customerId);
          } catch (notifError) {
            console.error(
              "Error creating assignment notification:",
              notifError,
            );
          }
        }
        // Keep phase status unchanged on assignment in single-worker model.
      } else {
        await staffAssignmentsAPI.deleteByCustomer(customerId);
      }
    });
    await Promise.all(assignmentPromises);
    if (!suppressToast) {
      if (memberId) {
        showToast(
          `${selectedIds.length} customer${selectedIds.length > 1 ? "s" : ""} assigned to ${member?.name || "member"} successfully`,
          "success",
        );
      } else {
        showToast(
          `Assignment removed for ${selectedIds.length} customer${selectedIds.length > 1 ? "s" : ""}`,
          "info",
        );
      }
    }
  };

  const handleBulkAssignSubmit = async ({
    regionId,
    clusterId,
    teamId,
    memberId,
    status,
  }) => {
    try {
      setError(null);
      setIsBulkSubmitting(true);

      const payload = {};
      if (regionId !== undefined) payload.region_id = regionId || null;
      if (clusterId !== undefined) payload.cluster_id = clusterId || null;
      if (teamId !== undefined) payload.team_id = teamId ?? null;
      if (status !== undefined) payload.status = status ?? null;

      if (Object.keys(payload).length > 0) {
        await Promise.all(
          selectedIds.map((id) => customersAPI.update(id, payload)),
        );
        if (payload.team_id === null) {
          await Promise.all(
            selectedIds.map((id) => staffAssignmentsAPI.deleteByCustomer(id)),
          );
        }
      }

      if (memberId !== undefined) {
        await runBulkMemberAssignment(memberId, { suppressToast: true });
      }

      const parts = [];
      if (regionId !== undefined) parts.push("region");
      if (clusterId !== undefined) parts.push("cluster");
      if (teamId !== undefined) parts.push("team");
      if (memberId !== undefined) parts.push("member");
      if (status !== undefined) parts.push("status");
      if (parts.length > 0) {
        showToast(
          `Assignments applied to ${selectedIds.length} customer(s): ${parts.join(", ")}`,
          "success",
        );
      }
      await fetchData();
      setSelectedIds([]);
    } catch (err) {
      if (err?.message !== "Ineligible customers") {
        console.error("Error applying bulk assignments:", err);
        setError(err.message || "Failed to apply assignments");
        showToast(
          `Failed to apply assignments: ${err.message || "Unknown error"}`,
          "error",
        );
        throw err;
      }
    } finally {
      setIsBulkSubmitting(false);
    }
  };

  const openRoleAssignModal = (customer, role) => {
    setRoleAssignModal({
      open: true,
      customer,
      role,
      memberId: "",
      saving: false,
    });
  };

  const closeRoleAssignModal = () => {
    if (roleAssignModal.saving) return;
    setRoleAssignModal({
      open: false,
      customer: null,
      role: null,
      memberId: "",
      saving: false,
    });
  };

  const handleConfirmRoleAssign = async () => {
    const customer = roleAssignModal.customer;
    const role = roleAssignModal.role;
    const memberId = roleAssignModal.memberId;

    if (!customer?.id || !role) return;
    if (!customer?.team_id) {
      showToast("Customer must be assigned to a team first", "error");
      return;
    }
    if (!memberId) {
      showToast(`Please select a ${role} team member.`, "error");
      return;
    }

    const member = teamMembers.find((m) => m.id === memberId);
    if (!member || member.role !== "worker") {
      showToast("Invalid worker selected.", "error");
      return;
    }

    const surveyApproved =
      (customer?.survey_approval_status || "").toLowerCase() === "approved";
    const civilApproved =
      (customer?.civil_approval_status || "").toLowerCase() === "approved";
    if (role === "civil" && !surveyApproved) {
      showToast(
        "Survey must be approved before assigning a civil team member.",
        "error",
      );
      return;
    }
    if (role === "technical" && !civilApproved) {
      showToast(
        "Civil work must be approved before assigning a technical team member.",
        "error",
      );
      return;
    }

    try {
      setRoleAssignModal((prev) => ({ ...prev, saving: true }));

      // Ensure single assignment per role for this customer
      const existingAssignments = await staffAssignmentsAPI.getByCustomerId(
        customer.id,
      );
      const roleAssignments = (existingAssignments || []).filter(
        (a) => a?.users?.role === "worker",
      );
      await Promise.all(
        roleAssignments.map((a) =>
          staffAssignmentsAPI.delete(customer.id, a.user_id),
        ),
      );

      // Create/refresh assignment
      await staffAssignmentsAPI.upsert(customer.id, memberId, {
        status: "assigned",
        assigned_at: new Date().toISOString(),
      });

      // Move status forward
      await customersAPI.update(customer.id, {
        status: role === "civil" ? "civil_pending" : "technical_pending",
      });

      // Notify
      try {
        await notificationsAPI.notifyUserOnAssignment(memberId, customer.id);
      } catch (notifError) {
        console.warn("Error creating assignment notification:", notifError);
      }

      showToast(
        `Customer "${customer?.name || "Unknown"}" assigned to ${member?.name || role} successfully`,
        "success",
      );

      closeRoleAssignModal();
      await fetchData();
      await fetchStats();
    } catch (err) {
      console.error("Error assigning by role:", err);
      showToast(
        `Failed to assign ${role}: ${err.message || "Unknown error"}`,
        "error",
      );
      setRoleAssignModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDownloadSurveyReport = async (customerId) => {
    try {
      showToast("Generating survey report...", "info");

      // Fetch all required data
      const [customer, survey] = await Promise.all([
        customersAPI.getById(customerId),
        surveysAPI.getByCustomerId(customerId).catch(() => null),
      ]);

      if (!survey) {
        showToast("No survey data found for this customer", "error");
        return;
      }

      // Get region and cluster
      const region = regions.find((r) => r.id === customer.region_id) || null;
      const cluster =
        clusters.find((c) => c.id === customer.cluster_id) || null;

      await generateSurveyReport(customer, survey, region, cluster);

      showToast("Survey report generated successfully", "success");
    } catch (error) {
      console.error("Error generating survey report:", error);
      showToast(
        error.message || "Failed to generate survey report. Please try again.",
        "error",
      );
    }
  };

  const handleBulkDownloadSurveyReports = async () => {
    try {
      if (selectedIds.length === 0) {
        showToast("Please select at least one customer", "error");
        return;
      }

      const selectedCustomers = customers.filter((c) =>
        selectedIds.includes(c.id),
      );
      if (selectedCustomers.length === 0) {
        showToast("No valid customers selected", "error");
        return;
      }

      showToast(
        `Generating ${selectedCustomers.length} survey report(s)... This may take a moment.`,
        "info",
      );

      let generated = 0;
      let skipped = 0;

      for (let i = 0; i < selectedCustomers.length; i++) {
        const customerRow = selectedCustomers[i];
        try {
          const [customerFull, survey] = await Promise.all([
            customersAPI.getById(customerRow.id).catch(() => customerRow),
            surveysAPI.getByCustomerId(customerRow.id).catch(() => null),
          ]);

          if (!survey) {
            skipped += 1;
            continue;
          }

          const region =
            regions.find((r) => r.id === customerFull.region_id) || null;
          const cluster =
            clusters.find((c) => c.id === customerFull.cluster_id) || null;

          await generateSurveyReport(customerFull, survey, region, cluster);
          generated += 1;

          // small delay so the browser can process downloads
          if (i < selectedCustomers.length - 1) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 400));
          }
        } catch (error) {
          console.error(
            `Error generating survey report for customer ${customerRow.id}:`,
            error,
          );
          skipped += 1;
        }
      }

      if (generated === 0) {
        showToast(
          "No survey reports generated (no survey data found).",
          "error",
        );
        return;
      }

      if (skipped > 0) {
        showToast(
          `Generated ${generated} survey report(s). Skipped ${skipped} (missing survey / errors).`,
          "success",
        );
      } else {
        showToast(`Generated ${generated} survey report(s).`, "success");
      }
    } catch (error) {
      console.error("Error generating bulk survey reports:", error);
      showToast(
        error.message || "Failed to generate survey reports. Please try again.",
        "error",
      );
    }
  };

  const handleDownloadWorkCompletionReport = async (customerId) => {
    try {
      showToast("Generating work completion report...", "info");

      // Fetch all required data
      const [customer, survey, civilWorkData, technicalWorkData, commissioningData] =
        await Promise.all([
          customersAPI.getById(customerId),
          surveysAPI.getByCustomerId(customerId).catch(() => null),
          civilWorkAPI.getByCustomerId(customerId).catch(() => null),
          technicalWorkAPI.getByCustomerId(customerId).catch(() => null),
          commissioningWorkAPI.getByCustomerId(customerId).catch(() => null),
        ]);

      // Get region and cluster
      const region = regions.find((r) => r.id === customer.region_id) || null;
      const cluster =
        clusters.find((c) => c.id === customer.cluster_id) || null;

      const wcrUsers = await getWcrUsers({ technicalWorkData, commissioningData });
      await generateWorkCompletionReport(
        customer,
        survey,
        civilWorkData,
        technicalWorkData,
        commissioningData,
        region,
        cluster,
        wcrUsers,
      );

      showToast("Work completion report generated successfully", "success");
    } catch (error) {
      console.error("Error generating work completion report:", error);
      showToast(
        error.message ||
          "Failed to generate work completion report. Please try again.",
        "error",
      );
    }
  };

  const handleDownloadPhotos = async (customerId) => {
    try {
      showToast("Preparing photo download...", "info");

      const [
        customer,
        survey,
        transportWorkData,
        civilWorkData,
        technicalWorkData,
        commissioningData,
      ] = await Promise.all([
        customersAPI.getById(customerId),
        surveysAPI.getByCustomerId(customerId).catch(() => null),
        transportWorkAPI.getByCustomerId(customerId).catch(() => null),
        civilWorkAPI.getByCustomerId(customerId).catch(() => null),
        technicalWorkAPI.getByCustomerId(customerId).catch(() => null),
        commissioningWorkAPI.getByCustomerId(customerId).catch(() => null),
      ]);

      const photoCount = await downloadCustomerPhotosZip(customer, {
        survey,
        transportWork: transportWorkData,
        civilWork: civilWorkData,
        technicalWork: technicalWorkData,
        commissioningWork: commissioningData,
      });

      if (photoCount === 0) {
        showToast("No photos found for this customer.", "error");
        return;
      }

      showToast(
        `Downloaded ${photoCount} photo${photoCount === 1 ? "" : "s"} as zip.`,
        "success",
      );
    } catch (error) {
      console.error("Error downloading customer photos:", error);
      showToast(
        error.message || "Failed to download photos. Please try again.",
        "error",
      );
    }
  };

  const handleBulkDownloadWorkCompletionReports = async () => {
    try {
      if (selectedIds.length === 0) {
        showToast("Please select at least one customer", "error");
        return;
      }

      // Customers ready for work completion report (technical or commissioning complete)
      const selectedCustomers = customers.filter(
        (c) =>
          selectedIds.includes(c.id) &&
          (c.status === "technical_complete" ||
            c.status === "commissioning_complete"),
      );

      if (selectedCustomers.length === 0) {
        showToast(
          "None of the selected customers are eligible. Choose customers with Technical Complete or Commissioning Complete status.",
          "error",
        );
        return;
      }

      if (selectedCustomers.length < selectedIds.length) {
        showToast(
          `Generating reports for ${selectedCustomers.length} eligible customer(s) (${selectedIds.length - selectedCustomers.length} skipped)`,
          "info",
        );
      }

      showToast(
        `Generating ${selectedCustomers.length} work completion report(s)... This may take a moment.`,
        "info",
      );

      // Generate reports one by one
      for (let i = 0; i < selectedCustomers.length; i++) {
        const customer = selectedCustomers[i];
        try {
          // Fetch all required data
          const [survey, civilWorkData, technicalWorkData, commissioningData] =
            await Promise.all([
              surveysAPI.getByCustomerId(customer.id).catch(() => null),
              civilWorkAPI.getByCustomerId(customer.id).catch(() => null),
              technicalWorkAPI.getByCustomerId(customer.id).catch(() => null),
              commissioningWorkAPI.getByCustomerId(customer.id).catch(() => null),
            ]);

          // Get region and cluster
          const region =
            regions.find((r) => r.id === customer.region_id) || null;
          const cluster =
            clusters.find((c) => c.id === customer.cluster_id) || null;

          const wcrUsers = await getWcrUsers({ technicalWorkData, commissioningData });
          await generateWorkCompletionReport(
            customer,
            survey,
            civilWorkData,
            technicalWorkData,
            commissioningData,
            region,
            cluster,
            wcrUsers,
          );

          // Small delay between reports to avoid browser blocking
          if (i < selectedCustomers.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(
            `Error generating report for customer ${customer.id}:`,
            error,
          );
        }
      }

      showToast(
        `Successfully generated ${selectedCustomers.length} work completion report(s)`,
        "success",
      );
    } catch (error) {
      console.error("Error generating bulk work completion reports:", error);
      showToast(
        error.message || "Failed to generate reports. Please try again.",
        "error",
      );
    }
  };

  const handleExportCustomers = async () => {
    try {
      const customersToExport =
        selectedIds.length > 0
          ? filteredCustomers.filter((c) => selectedIds.includes(c.id))
          : filteredCustomers;

      if (customersToExport.length === 0) {
        showToast("No customers to export", "error");
        return;
      }

      showToast("Exporting customer data...", "info");

      await exportCustomersToExcel(customersToExport, regions);

      showToast(
        `Successfully exported ${customersToExport.length} customer(s) to Excel`,
        "success",
      );
    } catch (error) {
      console.error("Error exporting customers:", error);
      showToast(
        error.message || "Failed to export customers. Please try again.",
        "error",
      );
    }
  };

  const handleDownloadQRCardsPDF = async () => {
    try {
      if (selectedIds.length === 0) {
        showToast("Please select at least one customer", "error");
        return;
      }

      // Get selected customers with their QR codes
      const selectedCustomers = customers.filter((c) =>
        selectedIds.includes(c.id),
      );

      const customersWithQR = selectedCustomers.filter((c) => c.qr_code);

      if (customersWithQR.length === 0) {
        showToast(
          "None of the selected customers have QR codes. Please generate QR codes first.",
          "error",
        );
        return;
      }

      if (customersWithQR.length < selectedCustomers.length) {
        showToast(
          `Generating PDF for ${customersWithQR.length} customer(s) with QR codes (${selectedCustomers.length - customersWithQR.length} without QR codes skipped)`,
          "info",
        );
      }

      showToast("Generating PDF... This may take a moment.", "info");

      await generateQRCardsPDF(customersWithQR, regions);

      showToast(
        `Successfully generated PDF with ${customersWithQR.length} QR card(s)`,
        "success",
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      showToast(
        error.message || "Failed to generate PDF. Please try again.",
        "error",
      );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <main className="content-area">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          {/* Header */}
          <div className="mb-6 lg:mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                  Customers
                </h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Manage customer records and field assignments
                </p>
                {error && (
                  <div className="mt-4 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                    {error}
                  </div>
                )}
              </div>

              {(user?.role === "super_admin" ||
                user?.role === "project_admin") && (
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    iconName="Upload"
                    iconPosition="left"
                    onClick={() => setIsUploadModalOpen(true)}
                    className="flex-1 sm:flex-initial"
                  >
                    Upload
                  </Button>
                  <Button
                    variant="outline"
                    iconName="QrCode"
                    iconPosition="left"
                    onClick={() => setIsGenerateQRModalOpen(true)}
                    className="flex-1 sm:flex-initial"
                  >
                    Generate QR
                  </Button>
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setIsAddModalOpen(true)}
                    className="flex-1 sm:flex-initial"
                  >
                    Add Customer
                  </Button>
                </div>
              )}
              {user?.role === "cluster_manager" && (
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    iconName="QrCode"
                    iconPosition="left"
                    onClick={() => setIsGenerateQRModalOpen(true)}
                    className="flex-1 sm:flex-initial"
                  >
                    Generate QR
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-4 lg:gap-6 mb-6 lg:mb-8 w-full">
            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Total Customers
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Icon
                    name="Users"
                    size={16}
                    className="text-blue-600 dark:text-blue-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.totalCustomers}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Survey Complete
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Icon
                    name="CheckCircle2"
                    size={16}
                    className="text-blue-600 dark:text-blue-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.surveyComplete}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Transport Complete
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Icon
                    name="Truck"
                    size={16}
                    className="text-amber-700 dark:text-amber-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.transportComplete}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Civil Complete
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Icon
                    name="CheckCircle2"
                    size={16}
                    className="text-purple-600 dark:text-purple-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.civilComplete}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Technical Complete
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Icon
                    name="CheckCircle2"
                    size={16}
                    className="text-green-600 dark:text-green-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.technicalComplete}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Commissioning Complete
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <Icon
                    name="CheckCircle2"
                    size={16}
                    className="text-teal-600 dark:text-teal-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.commissioningComplete}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 md:p-6 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs md:text-sm text-muted-foreground">
                  Unworkable
                </span>
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Icon
                    name="XCircle"
                    size={16}
                    className="text-red-600 dark:text-red-400"
                  />
                </div>
              </div>
              <p className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground data-text">
                {stats.unworkable}
              </p>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6 lg:mb-8">
            <FilterToolbar
              filters={filters}
              onFilterChange={setFilters}
              onSearch={handleSearch}
              onClearFilters={handleClearFiltersAndSearch}
              resultCount={filteredCustomers?.length}
              clusters={clusters}
              regions={regions}
              teams={teams}
              teamMembers={teamMembers}
            />
          </div>

          {/* Customer Table */}
          <div className="bg-card border border-border rounded-lg overflow-visible">
            {loading ? (
              <div className="p-12 text-center">
                <p className="text-muted-foreground">Loading customers...</p>
              </div>
            ) : (
              <>
                {/* Pagination Controls (top) */}
                {totalCustomers > 0 && (
                  <div className="p-4 border-b border-border">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          Items per page:
                        </span>
                        <Select
                          value={itemsPerPage.toString()}
                          onChange={(value) => {
                            setItemsPerPage(Number(value));
                            setCurrentPage(1);
                          }}
                          options={[
                            { value: "25", label: "25" },
                            { value: "50", label: "50" },
                            { value: "100", label: "100" },
                            { value: "500", label: "500" },
                          ]}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                          {Math.min(currentPage * itemsPerPage, totalCustomers)}{" "}
                          of {totalCustomers} entries
                        </span>
                      </div>
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={Math.max(
                          1,
                          Math.ceil(totalCustomers / itemsPerPage),
                        )}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </div>
                )}
                <CustomerTable
                  customers={filteredCustomers}
                  onEdit={handleEdit}
                  onDelete={handleDeleteCustomer}
                  onSelect={setSelectedIds}
                  selectedIds={selectedIds}
                  deletingId={deletingId}
                  regions={regions}
                  clusters={clusters}
                  teams={teams}
                  teamMembers={teamMembers}
                  onAssignTeam={handleAssignTeam}
                  onAssignMember={handleAssignMember}
                  onAssignCluster={handleAssignClusterToCustomer}
                  onAssignStatus={handleAssignStatus}
                  onQuickAssignByRole={handleQuickAssignByRole}
                  onOpenAssignRole={openRoleAssignModal}
                  allCustomers={filteredCustomers}
                  userRole={user?.role}
                  onDownloadQRCardsPDF={handleDownloadQRCardsPDF}
                  onExportCustomers={handleExportCustomers}
                  onBulkDelete={handleBulkDelete}
                  isBulkDeleting={isBulkDeleting}
                  onDownloadSurveyReport={handleDownloadSurveyReport}
                  onBulkDownloadSurveyReports={handleBulkDownloadSurveyReports}
                  onDownloadWorkCompletionReport={
                    handleDownloadWorkCompletionReport
                  }
                  onDownloadPhotos={handleDownloadPhotos}
                  onBulkDownloadWorkCompletionReports={
                    handleBulkDownloadWorkCompletionReports
                  }
                />

                {/* Pagination Controls (bottom) */}
                {totalCustomers > 0 && (
                  <div className="p-4 border-t border-border">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Left: Items per page and info */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-muted-foreground">
                          Items per page:
                        </span>
                        <Select
                          value={itemsPerPage.toString()}
                          onChange={(value) => {
                            setItemsPerPage(Number(value));
                            setCurrentPage(1);
                          }}
                          options={[
                            { value: "25", label: "25" },
                            { value: "50", label: "50" },
                            { value: "100", label: "100" },
                            { value: "500", label: "500" },
                          ]}
                          className="w-20"
                        />
                        <span className="text-sm text-muted-foreground">
                          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                          {Math.min(currentPage * itemsPerPage, totalCustomers)}{" "}
                          of {totalCustomers} entries
                        </span>
                      </div>
                      {/* Right: Pagination Controls */}
                      <PaginationControls
                        currentPage={currentPage}
                        totalPages={Math.max(
                          1,
                          Math.ceil(totalCustomers / itemsPerPage),
                        )}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Modals */}
      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        clusters={clusters}
        regions={regions}
        onSave={handleAddCustomer}
      />
      <ExcelUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
        clusters={clusters}
        regions={regions}
        projects={projects}
      />
      <GenerateQRModal
        isOpen={isGenerateQRModalOpen}
        onClose={() => setIsGenerateQRModalOpen(false)}
        customers={customers}
        clusters={clusters}
        regions={regions}
        teams={teams}
        onSuccess={fetchData}
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedIds?.length}
        onSubmitAssignments={handleBulkAssignSubmit}
        onClearSelection={() => setSelectedIds([])}
        regions={regions}
        clusters={clusters}
        teams={teams}
        teamMembers={teamMembers}
        userRole={user?.role}
        isSubmitting={isBulkSubmitting}
      />

      {/* Status Override Modal (Admin / Cluster Manager) */}
      {statusOverrideModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} className="text-amber-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Override status change
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    This bypasses normal phase progression rules.
                  </p>
                </div>
              </div>
              <button
                onClick={closeStatusOverrideModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={statusOverrideModal.saving}
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="text-sm text-foreground font-medium">
                  {statusOverrideModal.customer?.name || "—"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  CIN:{" "}
                  <span className="font-mono text-foreground">
                    {statusOverrideModal.customer?.cin || "—"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm">
                <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                  {getStatusLabel(statusOverrideModal.customer?.status)}
                </span>
                <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
                <span className="rounded-md bg-primary/10 px-2 py-1 font-mono text-xs text-primary">
                  {getStatusLabel(statusOverrideModal.newStatus)}
                </span>
              </div>

              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-muted-foreground">
                Approval or work records may not match this status.
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeStatusOverrideModal}
                  disabled={statusOverrideModal.saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  iconName="CheckCircle2"
                  iconPosition="left"
                  loading={statusOverrideModal.saving}
                  disabled={statusOverrideModal.saving}
                  onClick={handleConfirmStatusOverride}
                >
                  Apply override
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign by Role Modal (Civil/Technical) */}
      {roleAssignModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="UserPlus" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-foreground">
                    Assign to{" "}
                    {roleAssignModal.role === "civil" ? "Civil" : "Technical"}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    This will move status to{" "}
                    <span className="font-mono">
                      {roleAssignModal.role === "civil"
                        ? "civil_pending"
                        : "technical_pending"}
                    </span>
                    .
                  </p>
                </div>
              </div>
              <button
                onClick={closeRoleAssignModal}
                className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center"
                disabled={roleAssignModal.saving}
                aria-label="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-muted/30 border border-border rounded-lg p-4">
                <div className="text-sm text-foreground font-medium">
                  {roleAssignModal.customer?.name || "—"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  CIN:{" "}
                  <span className="font-mono text-foreground">
                    {roleAssignModal.customer?.cin || "—"}
                  </span>
                </div>
              </div>

              <Select
                label={`${roleAssignModal.role === "civil" ? "Civil" : "Technical"} team member`}
                value={roleAssignModal.memberId}
                onChange={(value) =>
                  setRoleAssignModal((prev) => ({ ...prev, memberId: value }))
                }
                searchable
                placeholder="Select member"
                options={(teamMembers || [])
                  .filter(
                    (m) =>
                      m?.role === roleAssignModal.role &&
                      m?.team_id === roleAssignModal.customer?.team_id,
                  )
                  .map((m) => ({
                    value: m.id,
                    label: `${m.unique_id || ""}${m.unique_id ? " - " : ""}${m.name || "Unnamed"}`,
                  }))}
              />

              <div className="text-xs text-muted-foreground">
                Limits:{" "}
                <span className="font-medium text-foreground">
                  {roleAssignModal.role === "civil" ? "30" : "10"}
                </span>{" "}
                active assignment(s) per member.
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button
                  variant="outline"
                  onClick={closeRoleAssignModal}
                  disabled={roleAssignModal.saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  iconName="CheckCircle2"
                  iconPosition="left"
                  loading={roleAssignModal.saving}
                  disabled={roleAssignModal.saving}
                  onClick={handleConfirmRoleAssign}
                >
                  Assign
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagination Controls Component
const PaginationControls = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 4) {
        // Near the start
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Near the end
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (!totalPages || totalPages < 1) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="ChevronLeft"
          disabled={currentPage === 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {getPageNumbers().map((page, index) => {
            if (page === "ellipsis") {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="px-2 text-muted-foreground"
                >
                  ...
                </span>
              );
            }
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === page
                    ? "bg-primary text-primary-foreground"
                    : "bg-background text-foreground hover:bg-muted border border-border"
                }`}
              >
                {page}
              </button>
            );
          })}
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="ChevronRight"
          iconPosition="right"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        >
          Next
        </Button>
      </div>
      <div className="text-sm text-muted-foreground whitespace-nowrap">
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

export default CustomerDatabase;
